// pages/social-login/telegramStart.ts — build the Telegram OIDC authorize URL (code flow + PKCE).
//
// Telegram is an OIDC provider (oauth.telegram.org); there is no client SDK. The FE runs the full-page
// code flow: it mints its own PKCE verifier + anti-forgery state, sends only the S256 challenge to
// Telegram, and posts the verifier to OUR backend on the callback. `state` is placed on the URL so it
// comes back on the redirect for the fail-closed anti-forgery check (see pkceState.evaluateCallback).

import { challengeFromVerifier, generateVerifier, mintState, type SsoAttempt, type SsoAttemptMode } from './pkceState';

const TELEGRAM_AUTHORIZE_URL = 'https://oauth.telegram.org/auth';

export interface TelegramAuthorize {
  /** The provider URL to navigate the browser to. */
  url: string;
  /** The attempt to persist (sessionStorage) before navigating, read back on the callback. */
  attempt: SsoAttempt;
}

/**
 * Build the Telegram authorize URL + the attempt to store. `returnTo` is the in-app route to restore
 * after the round-trip (the callback page falls back to the post-auth default when absent).
 * `sessionId` is the visitor's onboarding session, captured before the pre-sign-in logout, carried on
 * the attempt so the callback can bind it on the account (it cannot be re-read after the redirect).
 * `mode` marks whether this round-trip signs in ('login', default) or links the provider to the
 * already signed-in account ('link', the settings Connect flow); it is FE-only state on the attempt
 * and does not change the authorize URL.
 */
export async function buildTelegramAuthorize(
  clientId: string,
  redirectUri: string,
  returnTo?: string,
  sessionId?: string,
  mode: SsoAttemptMode = 'login',
): Promise<TelegramAuthorize> {
  const state = mintState();
  const verifier = generateVerifier();
  const challenge = await challengeFromVerifier(verifier);

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid phone',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  return {
    url: `${TELEGRAM_AUTHORIZE_URL}?${params.toString()}`,
    attempt: { provider: 'telegram', state, verifier, redirectUri, returnTo, sessionId, mode },
  };
}
